import React, { useState, useEffect, useContext } from 'react';
import { IterationContext, IterationContextType } from '../../contexts/IterationContext';
import Select from 'react-select';
import { debounce } from 'lodash';
import { getAttributeValues } from '../../services/IterationServices';
import Utils from 'Common/Utils';

type FilterModalProps = {
    isOpen: boolean;
    conditionId: string;
    onClose: () => void;
};

const FilterModal: React.FC<FilterModalProps> = ({
    isOpen,
    conditionId,
    onClose
}) => {
    const {
        iterationPayload,
        activeIterationId,
        attributeList,
        addFilter,
        removeFilter,
        flattenConditions,
        updateFilter
    } = useContext<IterationContextType>(IterationContext);

    const [filters, setFilters] = useState<Filter[]>([]);

    // Get current condition and its filters
    const currentIteration = iterationPayload?.iterations?.find(i => i.id === activeIterationId);
    const operationConditions = currentIteration ? flattenConditions(currentIteration.conditions) : [];
    const condition = operationConditions.find(c => c.id === conditionId);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);

    useEffect(() => {
        const hasIncompleteFilter = filters.some(filter => {
            return filter.attribute_id && (!filter.attribute_value_id || filter.attribute_value_id.trim() === '');
        });
        setIsSaveDisabled(hasIncompleteFilter);
    }, [filters]);

    // Initialize filters when modal opens
    useEffect(() => {
        if (isOpen && condition) {
            if (condition.filters.length > 0) {
                setFilters([...condition.filters]);
            } else {
                // Add one empty filter if none exist
                setFilters([{
                    id: `new-${Date.now()}`,
                    attribute_id: '',
                    attribute_name: '',
                    comparison_rule: '==',
                    attribute_value: '',
                    logical_operator: 'AND'
                }]);
            }
        }
    }, [isOpen, conditionId]);

    // Helper function to get already selected attribute value IDs for a given attribute
    const getSelectedAttributeValueIds = (attributeId: string, excludeFilterIndex?: number): string[] => {
        return filters
            .filter((filter, index) =>
                filter.attribute_id === attributeId &&
                filter.attribute_value_id &&
                (excludeFilterIndex === undefined || index !== excludeFilterIndex)
            )
            .map(filter => filter.attribute_value_id)
            .filter(Boolean) as string[];
    };

    const addNewFilter = () => {
        const newFilter: Filter = {
            id: `new-${Utils.generate_guid()}`,
            attribute_id: '',
            attribute_name: '',
            comparison_rule: '==',
            attribute_value: '',
            logical_operator: 'AND'
        };

        setFilters([...filters, newFilter]);
    };

    const removeFilterAtIndex = (index: number) => {
        if (filters.length > 1) {
            const newFilters = filters.filter((_, i) => i !== index);
            setFilters(newFilters);
        }
    };

    const updateFilterAtIndex = (index: number, updates: Partial<Filter>) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], ...updates };
        setFilters(newFilters);
    };

    const clearFilters = () => {
        setFilters([{
            id: `new-${Date.now()}`,
            attribute_id: '',
            attribute_name: '',
            comparison_rule: '==',
            attribute_value: '',
            logical_operator: 'AND'
        }]);
    };

    const applyFilters = () => {
        if (!activeIterationId) return;

        // Get current filters from the condition
        const currentFilters = condition?.filters || [];

        filters.forEach(filter => {
            if (filter.attribute_id) {
                // Check if this is an existing filter (has an ID that doesn't start with 'new-')
                const isExistingFilter = filter.id && !filter.id.startsWith('new-');

                if (isExistingFilter) {
                    // Update existing filter
                    updateFilter(activeIterationId, conditionId, filter.id, {
                        attribute_id: filter.attribute_id,
                        attribute_name: filter.attribute_name,
                        comparison_rule: filter.comparison_rule,
                        attribute_value_id: filter.attribute_value_id,
                        attribute_value: filter.attribute_value,
                        logical_operator: filter.logical_operator
                    });
                } else {
                    // Add new filter
                    addFilter(activeIterationId, conditionId, {
                        attribute_id: filter.attribute_id,
                        attribute_name: filter.attribute_name,
                        comparison_rule: filter.comparison_rule,
                        attribute_value_id: filter.attribute_value_id,
                        attribute_value: filter.attribute_value,
                        logical_operator: filter.logical_operator
                    });
                }
            }
        });

        // Remove filters that were deleted (present in current but not in filters state)
        const currentFilterIds = new Set(filters.map(f => f.id));
        currentFilters.forEach(currentFilter => {
            if (!currentFilterIds.has(currentFilter.id)) {
                removeFilter(activeIterationId, conditionId, currentFilter.id);
            }
        });

        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center" data-testid="filter-modal-overlay">
            <div className="tw-bg-white tw-shadow-xl tw-w-auto tw-max-w-5xl tw-max-h-[80%] tw-overflow-y-auto">
                {/* Header */}
                <div className="tw-pl-6 tw-pt-5 tw-pb-4 tw-border-b tw-border-zinc-300">
                    <h2 className="tw-text-base tw-font-semibold tw-text-blue-950">Add Filters</h2>
                </div>

                {/* Body */}
                <div className="tw-p-6 tw-overflow-y-auto">
                    {/* Column Headers */}
                    <div className="tw-flex tw-items-center tw-gap-6 tw-mb-2">
                        <div className="tw-w-12"></div>
                        <div className="tw-w-60">
                            <h3 className="tw-text-sm tw-font-medium tw-text-gray-700">Attribute</h3>
                        </div>
                        <div className="tw-w-36">
                            <h3 className="tw-text-sm tw-font-medium tw-text-gray-700">Comparison Rule</h3>
                        </div>
                        <div className="tw-w-60">
                            <h3 className="tw-text-sm tw-font-medium tw-text-gray-700">Attribute Value</h3>
                        </div>
                        <div className="tw-w-16"></div>
                    </div>

                    {/* Filter Rows */}
                    <div className="tw-space-y-4">
                        {filters.map((filter, index) => (
                            <React.Fragment key={filter.id}>
                                <FilterRow
                                    filter={filter}
                                    index={index}
                                    attributeList={attributeList}
                                    onUpdate={(updates) => updateFilterAtIndex(index, updates)}
                                    onRemove={() => removeFilterAtIndex(index)}
                                    onAdd={addNewFilter}
                                    canRemove={filters.length > 1}
                                    getSelectedAttributeValueIds={getSelectedAttributeValueIds}
                                />

                                {/* Logical Operator Row */}
                                {index < filters.length - 1 && (
                                    <div className="tw-flex tw-items-center tw-gap-6">
                                        <div className="tw-w-12"></div>
                                        <div className="tw-w-64">
                                            <select
                                                value={filter.logical_operator || 'AND'}
                                                onChange={(e) => updateFilterAtIndex(index, {
                                                    logical_operator: e.target.value as LogicalOperator
                                                })}
                                                className="tw-w-20 tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-zinc-300 tw-rounded-md tw-bg-white tw-text-gray-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500"
                                            >
                                                <option value="AND">And</option>
                                                <option value="OR">Or</option>
                                            </select>
                                        </div>
                                        <div className="tw-w-24"></div>
                                        <div className="tw-w-64"></div>
                                        <div className="tw-w-16"></div>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="tw-flex tw-justify-end tw-gap-6 tw-py-9 tw-pr-6">
                    <button
                        onClick={clearFilters}
                        className="link-primary tw-font-medium tw-text-[#091caa] text-center justify-start text-blue-800 tw-text-sm"
                        data-testid="clear-filters-button"
                    >
                        Clear Filters
                    </button>
                    <button
                        onClick={onClose}
                        className="outline-button"
                        data-testid="close-button"
                    >
                        Close
                    </button>
                    <button
                        onClick={applyFilters}
                        disabled={isSaveDisabled}
                        className="button-style"
                        data-testid="apply-filters-button"
                    >
                        Save Filters
                    </button>
                </div>
            </div>
        </div>
    );
};

// Filter Row Component
type FilterRowProps = {
    filter: Filter;
    index: number;
    attributeList: any[];
    onUpdate: (updates: Partial<Filter>) => void;
    onRemove: () => void;
    onAdd: () => void;
    canRemove: boolean;
    getSelectedAttributeValueIds: (attributeId: string, excludeFilterIndex?: number) => string[];
};

const FilterRow: React.FC<FilterRowProps> = ({
    filter,
    index,
    attributeList,
    onUpdate,
    onRemove,
    onAdd,
    canRemove,
    getSelectedAttributeValueIds
}) => {
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [options, setOptions] = useState<{ value: string, label: string }[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [noResults, setNoResults] = useState(false);

    // Fetch attribute values when attribute changes
    useEffect(() => {
        if (filter.attribute_id) {
            fetchAttributeValues(filter.attribute_id, page, searchValue);
        } else {
            resetState();
        }
    }, [filter.attribute_id, page, searchValue]);

    const fetchAttributeValues = async (attributeId: string, page: number, searchString = '') => {
        if (!attributeId) return;

        try {
            setIsLoading(true);
            const res = await getAttributeValues(attributeId, page, searchString);
            if (res?.done) {
                const attributeValues = res?.data[0]?.attribute_values ?? [];
                handleAttributeResponse(attributeValues, attributeId);
            }
        } catch (error) {
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAttributeResponse = (response: any[], attributeId: string) => {
        // Get already selected attribute value IDs for this attribute (excluding current filter)
        const selectedValueIds = getSelectedAttributeValueIds(attributeId, index);

        // Filter out already selected values
        const availableValues = response.filter(item =>
            !selectedValueIds.includes(item.id)
        );

        const newOptions = availableValues.map(item => ({
            value: item.id,
            label: item.value
        }));

        if (page === 1 && filter.attribute_value_id) {
            // Always include the current filter's selected value even if it would normally be filtered out
            const isCurrentValuePresent = newOptions.some(option => option.value === filter.attribute_value_id);
            if (!isCurrentValuePresent) {
                const currentValueOption = {
                    value: filter.attribute_value_id,
                    label: filter.attribute_value || ''
                };
                const updatedOptions = [currentValueOption, ...newOptions];
                setOptions(prevData => (page === 1 ? updatedOptions : [...prevData, ...updatedOptions]));
            } else {
                setOptions(prevData => (page === 1 ? newOptions : [...prevData, ...newOptions]));
            }
        } else {
            setOptions(prevData => (page === 1 ? newOptions : [...prevData, ...newOptions]));
        }

        setNoResults(newOptions.length === 0);
    };

    const loadMoreOptions = async () => {
        if (!noResults) {
            setPage(prevPage => prevPage + 1);
        } else {
            setIsLoading(false);
        }
    };

    const debouncedLoadMoreOptions = React.useCallback(
        debounce(() => loadMoreOptions(), 500),
        [page]
    );

    const debouncedSearch = debounce((value: string) => {
        setSearchValue(value);
        setPage(1);
    }, 500);

    const onInputChange = (input: string) => {
        if (input === '') {
            setPage(1);
        }
        debouncedSearch(input);
    };

    const handleMenuScrollToBottom = () => {
        setIsLoading(true);
        debouncedLoadMoreOptions();
    };

    const resetState = () => {
        setOptions([]);
        setNoResults(false);
        setPage(1);
        setSearchValue('');
    };

    const customStyles = {
        option: (provided: any) => ({ ...provided, textAlign: 'left', zIndex: 9999 }),
        control: (provided: any) => ({ ...provided, textAlign: 'left' }),
        menu: (provided: any) => ({
            ...provided,
            zIndex: 9999
        }),
        menuPortal: (provided: any) => ({
            ...provided,
            zIndex: 9999
        })
    };

    return (
        <div className="tw-flex tw-items-center tw-gap-6" data-testid={`filter-row-${index}`}>
            <div className="tw-w-12 tw-flex tw-justify-end">
                <span className="tw-text-sm tw-text-gray-500 tw-font-medium">when</span>
            </div>

            {/* Attribute Dropdown */}
            <div className="tw-w-60">
                <select
                    value={filter.attribute_id}
                    onChange={(e) => {
                        const attribute = attributeList.find(attr => attr.value === e.target.value);
                        onUpdate({
                            attribute_id: e.target.value,
                            attribute_name: attribute?.label || "",
                            attribute_value_id: '',
                            attribute_value: ''
                        });
                        resetState();
                    }}
                    className="tw-w-full tw-px-2.5 tw-py-2 tw-text-sm tw-rounded tw-outline tw-outline-1 tw-bg-white tw-text-gray-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-outline-offset-[-1px] tw-outline-zinc-200"
                >
                    <option value="">Select Attribute</option>
                    {attributeList.map(attr => (
                        <option key={attr.value} value={attr.value}>
                            {attr.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Comparison Rule Dropdown */}
            <div className="tw-w-36">
                <select
                    value={filter.comparison_rule}
                    onChange={(e) => onUpdate({ comparison_rule: e.target.value as ComparisonOperator })}
                    className="tw-w-full tw-px-2.5 tw-py-2 tw-text-sm tw-rounded tw-outline tw-outline-1 tw-bg-white tw-text-gray-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-outline-offset-[-1px] tw-outline-zinc-200"
                >
                    <option value="==">{"=="}</option>
                    <option value="!=">{"!="}</option>
                    <option value=">">{">"}</option>
                    <option value="<">{"<"}</option>
                    <option value=">=">{">="}</option>
                    <option value="<=">{"<="}</option>
                </select>
            </div>

            {/* Attribute Value Select with Search */}
            <div className="tw-w-60">
                <Select
                    options={options}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    onMenuScrollToBottom={handleMenuScrollToBottom}
                    isLoading={isLoading}
                    onInputChange={onInputChange}
                    value={filter.attribute_value_id ? { value: filter.attribute_value_id, label: filter.attribute_value } : null}
                    onChange={(selectedOption: any) => {
                        onUpdate({
                            attribute_value_id: selectedOption?.value || '',
                            attribute_value: selectedOption?.label || ''
                        });
                    }}
                    placeholder="Select Value"
                    maxMenuHeight={220}
                    menuPlacement="auto"
                    isClearable={true}
                    components={{ IndicatorSeparator: () => null }}
                    isDisabled={!filter.attribute_id}
                />
            </div>

            {/* Action Buttons */}
            <div className="tw-w-16 tw-flex tw-gap-6">
                <button
                    onClick={onAdd}
                    className="delete-button"
                    data-testid={`add-filter-${index}`}
                    title="Add Filter"
                >
                    <i
                        className="fa fa-plus"
                        aria-hidden="true"
                        style={{ color: "rgb(9, 28, 170)" }}
                    />
                </button>
                {canRemove && (
                    <button
                        onClick={onRemove}
                        className="delete-button"
                        data-testid={`remove-filter-${index}`}
                        title="Remove Filter"
                    >
                        <i
                            className="fa fa-minus"
                            aria-hidden="true"
                            style={{ color: "rgb(9, 28, 170)" }}
                        />
                    </button>
                )}
            </div>
        </div>
    );
};

export default FilterModal;